import "@app/App.css";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { TW_ObjectModelViewHeaderButton, TW_ObjectModelViewHeaderSmallerIcon } from "@utils/twClasses";
import { formatNextUndoLabel, performRedo, performUndo, refreshUndoState, UndoResult, UndoState, useUndoStore } from "@utils/undo";
import { isEditingTarget, isInDialog } from "@utils/editGuard";
import { Undo } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useDataStore, useModelStore } from "src/store";

function UndoButton() {
    const { t } = useTranslation();
    const workspace = useModelStore((state) => state.selectedWorkspace);
    const objectInfos = useModelStore((state) => state.selectedObjectInfos);
    const view = useModelStore((state) => state.selectedView);
    const fetchData = useDataStore((state) => state.fetchData);
    const loadData = useDataStore((state) => state.loadData);
    const stopLoadingData = useDataStore((state) => state.stopLoadingData);
    const applyCellValues = useDataStore((state) => state.applyCellValues);
    const loadingData = useDataStore((state) => state.loadingData);
    const rowActionsEnabled = useDataStore((state) => state.rowActionsEnabled);

    const objectKey = objectInfos?.object._key;
    const workspaceKey = workspace?._key;
    const viewKey = view?._key;

    const state = useUndoStore((s) => (objectKey ? s.byObject[objectKey] : undefined));

    // Reconciliation point: fetch the authoritative state whenever we enter a
    // view/object (and on mount / reload). The hot mutation path stays optimistic
    // (markUndoable), this corrects labels and catches anything done elsewhere.
    useEffect(() => {
        if (workspaceKey && objectKey) {
            refreshUndoState(workspaceKey, objectKey);
        }
    }, [workspaceKey, objectKey, viewKey]);

    // Keep the latest scope/state in a ref so the keyboard listener can be
    // attached exactly once (no re-attach churn, no double firing).
    const ctxRef = useRef<{ workspaceKey?: string; objectKey?: string; state?: UndoState }>({});
    ctxRef.current = { workspaceKey, objectKey, state };

    // Refresh after an undo/redo, awaited so the spinner stays up until the
    // rows are actually refreshed. Applied cell values are still pushed
    // optimistically so the grid is correct the instant the overlay lifts.
    const doRefresh = async (result: UndoResult) => {
        if (result.cells && result.cells.length > 0) {
            applyCellValues(result.cells);
        }
        await fetchData();
    };

    const runUndo = () => {
        const { workspaceKey, objectKey, state } = ctxRef.current;
        if (!workspaceKey || !objectKey) {
            return;
        }
        // Ignore Ctrl-Z while the spinner is up (an undo/redo or any other grid
        // load is already in flight) — prevents queueing a second undo. Likewise
        // while a record is being created: it would delete the record under the row.
        if (useDataStore.getState().loadingData || !useDataStore.getState().rowActionsEnabled) {
            return;
        }
        if (!state?.canUndo) {
            toast.info(t("undo.nothing"));
            return;
        }
        // Spinner from the keypress/click until the grid rows are refreshed.
        loadData();
        performUndo(workspaceKey, objectKey, doRefresh).finally(() => stopLoadingData());
    };

    const runRedo = () => {
        const { workspaceKey, objectKey, state } = ctxRef.current;
        if (!workspaceKey || !objectKey) {
            return;
        }
        if (useDataStore.getState().loadingData || !useDataStore.getState().rowActionsEnabled) {
            return;
        }
        if (!state?.canRedo) {
            toast.info(t("redo.nothing"));
            return;
        }
        loadData();
        performRedo(workspaceKey, objectKey, doRefresh).finally(() => stopLoadingData());
    };

    // Application-level Ctrl-Z / Ctrl-Y, attached once. Skipped while editing a
    // cell (native browser undo wins), in a dialog (the grid is behind it) and
    // on auto-repeat.
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            const mod = e.ctrlKey || e.metaKey;
            if (!mod || e.repeat || isEditingTarget(e.target) || isInDialog(e.target)) {
                return;
            }
            const key = e.key.toLowerCase();
            const isRedo = key === "y" || (key === "z" && e.shiftKey);
            const isUndo = key === "z" && !e.shiftKey;
            if (isUndo) {
                e.preventDefault();
                runUndo();
            } else if (isRedo) {
                e.preventDefault();
                runRedo();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The visible button appears only when there is something to undo.
    if (!workspaceKey || !objectKey || !state?.canUndo) {
        return null;
    }

    const labelText = formatNextUndoLabel(state);
    const tooltip = (labelText ? `${t("undo.tooltip")} : ${labelText}` : t("undo.tooltip")) + " (Ctrl+Z)";

    return (
        <button
            type="button"
            className={TW_ObjectModelViewHeaderButton + ((loadingData || !rowActionsEnabled) ? " opacity-50 cursor-default" : " opacity-100 cursor-default")}
            onClick={runUndo}
            disabled={loadingData || !rowActionsEnabled}
            aria-label={tooltip}
        >
            <TooltipProvider>
                <Tooltip delayDuration={100}>
                    <TooltipTrigger asChild>
                        <Undo className={TW_ObjectModelViewHeaderSmallerIcon} />
                    </TooltipTrigger>
                    <TooltipContent>{tooltip}</TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </button>
    );
}

export default UndoButton;
